
import { useState, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { httpsCallable } from 'firebase/functions';
import { FetchState } from '../global/types';
import {
    collection,
    query,
    where,
    getDocs,
    getDoc,
    deleteDoc,
    doc,
    orderBy,
    limit,
    startAfter,
    Timestamp,
    QueryConstraint,
    writeBatch
} from 'firebase/firestore';
import { db, functions } from '../firebaseConfig';
import { LedgerTransaction, TransactionStatus, TransactionType } from '../global/types';
import { checkBudgetOverspending } from './budgetAlertService';
import { fetchUserSettings } from './usersServices';
import { showWarningNotification } from '../utils/notificationUtils';
import i18n from '../i18n';
import { removeUndefined } from '@/utils/misc';
import { notifyLedgerChanged } from '../utils/ledgerEvents';
import { resolveReportingCurrency } from '../utils/reportingCurrency';
import { CALCULATION_CURRENCY } from '../utils/currencyUtils';

const COLLECTION_NAME = 'ledger_transactions';

type CommitLedgerTransactionResponse = {
    id: string;
    systemAmountUsd: number;
    fxRate: number;
    fxRateDate: string;
    fxRateSource: string;
    fxFallbackFromDate?: string;
};

const commitLedgerTransactionFn = httpsCallable<Record<string, unknown>, CommitLedgerTransactionResponse>(functions, 'commitLedgerTransaction');

export type LedgerRowRejection = {
    /** Index in the rows array as sent. */
    index: number;
    reason: 'currency-mismatch' | 'no-rate' | 'not-owner';
    detail: string;
};

export type LedgerBatchResult = {
    /** Document ids in the order the rows were sent; null where the row was rejected. */
    ids: (string | null)[];
    written: number;
    /** Written without a Money Snapshot because the row's day has not come yet. */
    pending: number;
    /** Rows the server refused, with the reason, so the caller can show them. */
    rejected: LedgerRowRejection[];
};

/** Writes many ledger rows at once; the server denominates and snapshots each one. */
export const commitLedgerTransactionsBatchFn = httpsCallable<{ rows: Record<string, unknown>[] }, LedgerBatchResult>(
    functions,
    'commitLedgerTransactionsBatch'
);

export const toEpochMs = (date: Date | Timestamp): number => (date instanceof Date ? date.getTime() : date.toMillis());

const notifyBudgetStatus = (result: Awaited<ReturnType<typeof checkBudgetOverspending>>) => {
    if (!result) return;
    const params = { spent: result.spent.toFixed(2), budget: result.budget.toFixed(2), percent: result.percentUsed.toFixed(0) };
    if (result.isOverBudget) {
        showWarningNotification(i18n.t('budget_alerts.exceeded', params));
    } else if (result.isApproachingLimit) {
        showWarningNotification(i18n.t('budget_alerts.approaching', params));
    }
};


// TODO : Store locally the preference for budget alerts to avoid fetching user settings every time a transaction is added/updated. This can be done via a context or a global state management solution.
const checkBudgetForTransaction = async (tx: Pick<LedgerTransaction, 'userId' | 'categoryId' | 'amount' | 'currency' | 'date'>, excludeTransactionId?: string) => {
    try {
        const settings = await fetchUserSettings(tx.userId);
        if (!settings?.preferences?.enableBudgetAlerts) return;
        const resolution = resolveReportingCurrency(settings);
        const reportingCurrency = 'code' in resolution ? resolution.code : CALCULATION_CURRENCY;
        notifyBudgetStatus(await checkBudgetOverspending(tx.userId, tx, reportingCurrency, excludeTransactionId));
    } catch (error) {
        console.error('Error checking budget:', error);
    }
};

// --- Fetching ---

export const fetchLedgerTransactions = async (
    userId: string,
    filters?: {
        accountId?: string;
        startDate?: Date;
        endDate?: Date;
        type?: TransactionType;
        status?: TransactionStatus;
        limit?: number;
    }
): Promise<LedgerTransaction[]> => {
    const ref = collection(db, COLLECTION_NAME);
    const constraints: QueryConstraint[] = [
        where('userId', '==', userId),
        orderBy('date', 'desc')
    ];

    if (filters?.accountId) constraints.push(where('accountId', '==', filters.accountId));
    if (filters?.startDate) constraints.push(where('date', '>=', Timestamp.fromDate(filters.startDate)));
    if (filters?.endDate) constraints.push(where('date', '<=', Timestamp.fromDate(filters.endDate)));
    if (filters?.type) constraints.push(where('type', '==', filters.type));
    if (filters?.status) constraints.push(where('status', '==', filters.status));
    if (filters?.limit) constraints.push(limit(filters.limit));

    const q = query(ref, ...constraints);
    const snap = await getDocs(q);

    return snap.docs.map(d => ({ ...d.data(), id: d.id })) as LedgerTransaction[];
};

// --- Mutations ---

export const addLedgerTransaction = async (tx: Omit<LedgerTransaction, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
    Sentry.addBreadcrumb({
        category: 'ledger',
        message: 'Adding ledger transaction',
        data: {
            amount: tx.amount,
            type: tx.type,
            category: tx.categoryGroup
        },
        level: 'info'
    });

    const date = tx.date instanceof Date ? Timestamp.fromDate(tx.date) : tx.date;
    await checkBudgetForTransaction({ ...tx, date });

    const result = await commitLedgerTransactionFn(removeUndefined({ ...tx, date: toEpochMs(date) }));
    notifyLedgerChanged();
    return result.data.id;
};

export const updateLedgerTransaction = async (id: string, updates: Partial<LedgerTransaction>) => {
    Sentry.addBreadcrumb({
        category: 'ledger',
        message: 'Updating ledger transaction',
        data: { id, updates: Object.keys(updates) },
        level: 'info'
    });

    // commitLedgerTransaction always recomputes the snapshot from accountId/amount/currency/date,
    // so those are needed on every call, not just when the patch touches them.
    const currentSnap = await getDoc(doc(db, COLLECTION_NAME, id));
    const current = currentSnap.exists() ? (currentSnap.data() as LedgerTransaction) : null;
    if (!current) {
        throw new Error(`Ledger transaction ${id} not found`);
    }

    if (updates.amount !== undefined || updates.categoryId !== undefined || updates.date !== undefined) {
        await checkBudgetForTransaction({
            userId: current.userId,
            categoryId: updates.categoryId ?? current.categoryId,
            amount: updates.amount ?? current.amount,
            currency: updates.currency ?? current.currency,
            date: updates.date ?? current.date,
        }, id);
    }

    const date = updates.date ?? current.date;
    const result = await commitLedgerTransactionFn(removeUndefined({
        ...updates,
        id,
        accountId: updates.accountId ?? current.accountId,
        amount: updates.amount ?? current.amount,
        currency: updates.currency ?? current.currency,
        date: toEpochMs(date)
    }));
    notifyLedgerChanged();
    return result.data;
};

export const deleteLedgerTransaction = async (id: string) => {
    Sentry.addBreadcrumb({
        category: 'ledger',
        message: 'Deleting ledger transaction',
        data: { id },
        level: 'info'
    });
    await deleteDoc(doc(db, COLLECTION_NAME, id));
    notifyLedgerChanged();
};

const MONEY_FIELDS: (keyof LedgerTransaction)[] = ['amount', 'currency', 'date'];

/** The bulk-edit form hands back a date as the raw `<input type="date">` string. */
const asDate = (value: unknown): Date | Timestamp =>
    typeof value === 'string' ? new Date(value) : (value as Date | Timestamp);

/**
 * Returns the rows the server refused, so the caller can say so. A patch that moves money goes
 * through the server like a single edit does: written from here the rows would keep the Money
 * Snapshot of their old amount and read wrong in every converted total.
 */
export const bulkUpdateLedgerTransaction = async (
    ids: string[],
    updates: Partial<LedgerTransaction>
): Promise<LedgerRowRejection[]> => {
    const safeUpdates = removeUndefined(updates) as Partial<LedgerTransaction>;
    const movesMoney = MONEY_FIELDS.some((field) => safeUpdates[field] !== undefined);

    if (!movesMoney) {
        const batch = writeBatch(db);
        ids.forEach(id => {
            batch.update(doc(db, COLLECTION_NAME, id), { ...safeUpdates, updatedAt: Timestamp.now() });
        });
        await batch.commit();
        notifyLedgerChanged();
        return [];
    }

    // The callable recomputes the snapshot from accountId/amount/currency/date, so the fields the
    // patch leaves alone have to travel with it.
    const snaps = await Promise.all(ids.map((id) => getDoc(doc(db, COLLECTION_NAME, id))));
    const rows = snaps
        .filter((snap) => snap.exists())
        .map((snap) => {
            const current = snap.data() as LedgerTransaction;
            return removeUndefined({
                ...safeUpdates,
                id: snap.id,
                accountId: current.accountId,
                amount: safeUpdates.amount ?? current.amount,
                currency: safeUpdates.currency ?? current.currency,
                date: toEpochMs(asDate(safeUpdates.date ?? current.date))
            });
        });

    const { data } = await commitLedgerTransactionsBatchFn({ rows });
    notifyLedgerChanged();
    return data.rejected;
};

export function useFetchMonthLedgerTransactions(userId: string | null, month: number | null, year: number, isRelative: boolean = false): FetchState<LedgerTransaction[]> {
    const [data, setData] = useState<LedgerTransaction[] | null>(null);
    const [loading, setLoading] = useState<boolean>(!!userId);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!userId) {
            setData([]);
            setLoading(false);
            setError(null);
            return;
        }

        const fetchTransactions = async () => {
            setLoading(true);
            setError(null);
            try {
                // Determine target month/year
                const currentYear = new Date().getFullYear();
                const targetYear = isRelative ? currentYear - year : year;

                // month is likely 1-indexed (1=Jan, 12=Dec) based on usage context
                // Date constructor expects 0-indexed month (0=Jan, 11=Dec)
                // If isRelative is true, typically 'month' might be null?
                // Let's stick to the logic from variableTransactionServices for consistency.

                const startDate = new Date(targetYear, month ? month - 1 : 0, 1);
                const endDate = isRelative ? new Date() : new Date(targetYear, month ? month : 12, 0, 23, 59, 59);

                const transactions = await fetchLedgerTransactions(userId, {
                    startDate,
                    endDate
                });

                // Default filter: Exclude SCHEDULED
                const filtered = transactions.filter(t => t.status !== TransactionStatus.SCHEDULED);

                setData(filtered);
            } catch (err) {
                console.error('Error fetching ledger transactions:', err);
                setError('Failed to fetch ledger transactions for month');
                setData([]);
            } finally {
                setLoading(false);
            }
        };

        fetchTransactions();
    }, [userId, month, year, isRelative]);

    return { data, loading, error };
}

export function useFetchLedgerTransactionsInRange(userId: string | null, startDate: Date | null, endDate: Date | null): FetchState<LedgerTransaction[]> {
    const [data, setData] = useState<LedgerTransaction[] | null>(null);
    const [loading, setLoading] = useState<boolean>(!!userId);
    const [error, setError] = useState<string | null>(null);

    const startTime = startDate ? startDate.getTime() : null;
    const endTime = endDate ? endDate.getTime() : null;

    useEffect(() => {
        if (!userId || startTime === null || endTime === null) {
            setData([]);
            setLoading(false);
            setError(null);
            return;
        }

        let cancelled = false;

        const fetchTransactions = async () => {
            setLoading(true);
            setError(null);
            try {
                const transactions = await fetchLedgerTransactions(userId, {
                    startDate: new Date(startTime),
                    endDate: new Date(endTime)
                });

                if (cancelled) return;

                // Default filter: Exclude SCHEDULED (same default as useFetchMonthLedgerTransactions)
                const filtered = transactions.filter(t => t.status !== TransactionStatus.SCHEDULED);

                setData(filtered);
            } catch (err) {
                if (cancelled) return;
                console.error('Error fetching ledger transactions for range:', err);
                setError('Failed to fetch ledger transactions for range');
                setData([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchTransactions();

        return () => {
            cancelled = true;
        };
    }, [userId, startTime, endTime]);

    return { data, loading, error };
}
